import { useEffect, useMemo, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Spinner } from '../../components/atoms/Spinner'
import { TENANT } from '../../../constants/store.constants'
import { useOrganisations, type OrganisationDto } from '../../../viewmodels/useOrganisations'
import {
  useBuildRequestDetail,
  type BuildRequestItemDto,
  type BuildRequestItemReplaceInput
} from '../../../viewmodels/useBuildRequestDetail'

/**
 * BuildRequestEditModal — operator review modal for one build_request.
 *
 * Renders inside the shared `<Modal>` atom (Kiran 2026-06-08) so the
 * chrome (backdrop, header, footer, motion, mobile bottom-sheet
 * behaviour) matches every other modal in the app — same close button
 * pattern, same Cancel-on-the-right footer, same cinematic entrance.
 *
 * Body content:
 *   - read-only customer info (name, email, phone)
 *   - editable organisation dropdown (master excluded — see
 *     TENANT.MASTERORG_ID filter)
 *   - editable max_allowed_count (org-level)
 *   - editable line set: per-line quantity stepper + Remove button;
 *     "+ Add line" appends a row with manual variant_id/product_id
 *     entry (v1 — a product picker is a separate iteration).
 *
 * Footer actions:
 *   - Close   — always.
 *   - Confirm — when in 'requested'; flips status='confirmed' so the
 *               row becomes Publishable on the list page.
 *   - Decommission URL — when in 'built'; detaches the CF custom
 *               domain. D1 state preserved.
 *
 * Cancel + Publish live on the list page as per-row inline buttons —
 * Cancel hits any non-terminal row, Publish provisions the subdomain
 * once status='confirmed'.
 */
export function BuildRequestEditModal({
  id,
  onClose
}: {
  readonly id: string
  readonly onClose: (refreshList: boolean) => void
}): JSX.Element {
  const vm = useBuildRequestDetail(id)
  const orgsVm = useOrganisations()
  // Local edit state — kept in sync with detail on load; flushed via patchMeta.
  const [orgId, setOrgId] = useState<string>('')
  const [maxAllowed, setMaxAllowed] = useState<string>('')
  // Cart-value cap captured in RUPEES on the form (operators think in
  // rupees, not paisa). Converted to paisa before sending. Migration
  // 0111 stores the column as paisa per Rule #16.
  const [maxCartValueRupees, setMaxCartValueRupees] = useState<string>('')
  const [editLines, setEditLines] = useState<EditLine[]>([])
  const [didDirty, setDidDirty] = useState(false)
  const [actionMsg, setActionMsg] = useState<string | null>(null)
  const [actionErr, setActionErr] = useState<string | null>(null)

  useEffect(() => {
    if (vm.detail === null) return
    setOrgId(vm.detail.organisation_id)
    setMaxAllowed(vm.detail.max_allowed_count !== null ? String(vm.detail.max_allowed_count) : '')
    setMaxCartValueRupees(
      vm.detail.max_cart_value_paisa !== null ? String(vm.detail.max_cart_value_paisa / 100) : ''
    )
    setEditLines(vm.detail.items.map(toEditLine))
    setDidDirty(false)
    setActionErr(null)
  }, [vm.detail])

  const terminal = vm.detail !== null && (vm.detail.status === 'built' || vm.detail.status === 'cancelled')
  // The master org is the seeded Uyyamadilu parent — the org the
  // customer was on when they submitted. The operator must assign a
  // real (non-master) org before the row can be Published.
  const assignableOrgs = useMemo(
    () => orgsVm.items.filter((o) => o.id !== TENANT.MASTERORG_ID),
    [orgsVm.items]
  )
  const orgIsAssigned = orgId !== '' && orgId !== TENANT.MASTERORG_ID
  const canConfirm = vm.detail !== null && vm.detail.status === 'requested'
  const isBuilt = vm.detail !== null && vm.detail.status === 'built'

  const totalCount = useMemo(() => editLines.reduce((acc, l) => acc + (l.quantity ?? 0), 0), [editLines])

  const handleClose = (): void => onClose(didDirty)

  async function saveMeta(): Promise<void> {
    setActionErr(null)
    setActionMsg(null)
    if (vm.detail === null) return
    const maxAsNum = maxAllowed.trim() === '' ? null : Number(maxAllowed)
    if (maxAsNum !== null && (!Number.isFinite(maxAsNum) || !Number.isInteger(maxAsNum) || maxAsNum < 0)) {
      setActionErr('Max allowed count must be a non-negative integer')
      return
    }
    const cartPaisa = cartValueAsPaisa(maxCartValueRupees)
    if (cartPaisa === 'invalid') {
      setActionErr('Max cart value (₹) must be a non-negative number')
      return
    }
    const patch: {
      organisation_id?: string
      max_allowed_count?: number | null
      max_cart_value_paisa?: number | null
    } = {}
    if (orgId !== vm.detail.organisation_id) patch.organisation_id = orgId
    if (maxAsNum !== vm.detail.max_allowed_count) patch.max_allowed_count = maxAsNum
    if (cartPaisa !== vm.detail.max_cart_value_paisa) patch.max_cart_value_paisa = cartPaisa
    if (Object.keys(patch).length === 0) return
    const r = await vm.patchMeta(patch)
    if (!r.ok) { setActionErr(r.message); return }
    setActionMsg('Saved')
    setDidDirty(true)
  }

  async function saveLines(): Promise<void> {
    setActionErr(null)
    setActionMsg(null)
    const out: BuildRequestItemReplaceInput[] = []
    for (const l of editLines) {
      if (l.variant_id.trim() === '' || l.product_id.trim() === '' || l.product_name.trim() === '') {
        setActionErr('Every line needs variant_id, product_id, product_name')
        return
      }
      if (l.quantity === null || l.quantity < 1) {
        setActionErr(`Quantity must be ≥ 1 (${l.product_name})`)
        return
      }
      out.push({
        variant_id: l.variant_id.trim(),
        product_id: l.product_id.trim(),
        product_name: l.product_name.trim(),
        axes_label: l.axes_label.trim() === '' ? null : l.axes_label.trim(),
        quantity: l.quantity
      })
    }
    const r = await vm.replaceItems(out)
    if (!r.ok) { setActionErr(r.message); return }
    setActionMsg('Lines updated')
    setDidDirty(true)
  }

  // Confirm bundles every pending edit into one atomic PATCH (Kiran
  // 2026-06-08). Earlier this just sent { status: 'confirmed' } and
  // relied on the operator clicking "Save organisation + max allowed
  // count" beforehand — so a dropdown pick that wasn't separately
  // saved silently got dropped on Confirm. Now: validate, then send
  // org + max + status together. Failures keep the modal open + show
  // the error inline; success closes the modal + reloads the list.
  async function confirmStatus(): Promise<void> {
    setActionErr(null)
    setActionMsg(null)
    if (vm.detail === null) return
    if (!orgIsAssigned) {
      setActionErr('Pick a tenant organisation before confirming.')
      return
    }
    const maxAsNum = maxAllowed.trim() === '' ? null : Number(maxAllowed)
    if (maxAsNum === null) {
      setActionErr('Set the max allowed count before confirming.')
      return
    }
    if (!Number.isFinite(maxAsNum) || !Number.isInteger(maxAsNum) || maxAsNum < 0) {
      setActionErr('Max allowed count must be a non-negative integer')
      return
    }
    const cartPaisa = cartValueAsPaisa(maxCartValueRupees)
    if (cartPaisa === 'invalid') {
      setActionErr('Max cart value (₹) must be a non-negative number')
      return
    }
    if (cartPaisa === null) {
      setActionErr('Set the max cart value (₹) before confirming.')
      return
    }
    const patch: {
      status: 'confirmed'
      organisation_id?: string
      max_allowed_count?: number | null
      max_cart_value_paisa?: number | null
    } = { status: 'confirmed' }
    if (orgId !== vm.detail.organisation_id) patch.organisation_id = orgId
    if (maxAsNum !== vm.detail.max_allowed_count) patch.max_allowed_count = maxAsNum
    if (cartPaisa !== vm.detail.max_cart_value_paisa) patch.max_cart_value_paisa = cartPaisa
    const r = await vm.patchMeta(patch)
    if (!r.ok) { setActionErr(r.message); return }
    onClose(true)
  }

  async function decommission(): Promise<void> {
    setActionErr(null)
    setActionMsg(null)
    if (!window.confirm(
      `Decommission ${vm.detail?.subdomain ?? '(this)'}.uyyamadilu.com? ` +
      `The URL stops responding. All backend state (status, customers, ` +
      `carts, orders) stays as-is — only the public URL goes dark.`
    )) return
    const r = await vm.decommission()
    if (!r.ok) { setActionErr(r.message); return }
    setActionMsg(`URL revoked — backend state preserved.`)
    setDidDirty(true)
  }

  const titleSuffix = vm.detail !== null ? ` — ${vm.detail.customer_email}` : ''

  return (
    <Modal
      open
      onClose={handleClose}
      title={`Build request${titleSuffix}`}
      maxWidth="3xl"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>Close</Button>
          {isBuilt ? (
            <Button variant="ghost" onClick={decommission}>Decommission URL</Button>
          ) : null}
          {canConfirm ? (
            <Button variant="primary" onClick={confirmStatus}>Confirm</Button>
          ) : null}
        </>
      }
    >
      {vm.isLoading && vm.detail === null ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : vm.error !== null ? (
        <Alert tone="error">{vm.error}</Alert>
      ) : vm.detail === null ? (
        <Alert tone="error">Build request not found.</Alert>
      ) : (
        <div className="space-y-5">
          {actionErr !== null ? <Alert tone="error">{actionErr}</Alert> : null}
          {actionMsg !== null ? <Alert tone="info">{actionMsg}</Alert> : null}

          {isBuilt ? (
            <Alert tone="info">
              Live at <span className="font-mono">{vm.detail.subdomain}.uyyamadilu.com</span>
            </Alert>
          ) : null}

          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Submitted by">{vm.detail.customer_first_name} {vm.detail.customer_last_name}</Field>
            <Field label="Email">{vm.detail.customer_email}</Field>
            <Field label="Phone">{vm.detail.customer_phone ?? '—'}</Field>
            <Field label="Status">{vm.detail.status}</Field>
            <Field label="Requested at">{formatDate(vm.detail.requested_at)}</Field>
          </section>

          <section className="border-t border-border/40 pt-4 space-y-3">
            <label className="block">
              <span className="font-body text-body-xs uppercase tracking-wide text-text-secondary">Organisation</span>
              <select
                value={orgIsAssigned ? orgId : ''}
                disabled={terminal}
                onChange={(e) => setOrgId(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-bg-primary p-2 font-body text-body-sm text-charcoal disabled:bg-bg-secondary disabled:text-text-secondary"
              >
                <option value="" disabled>— Select organisation —</option>
                {assignableOrgs.map((o: OrganisationDto) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-body text-body-xs uppercase tracking-wide text-text-secondary">Max allowed count</span>
              <Input
                type="number"
                min={0}
                step={1}
                value={maxAllowed}
                disabled={terminal}
                onChange={(e) => setMaxAllowed(e.target.value)}
                placeholder="e.g. 500"
              />
            </label>
            <label className="block">
              <span className="font-body text-body-xs uppercase tracking-wide text-text-secondary">
                Max cart value (₹)
              </span>
              <Input
                type="number"
                min={0}
                step={1}
                value={maxCartValueRupees}
                disabled={terminal}
                onChange={(e) => setMaxCartValueRupees(e.target.value)}
                placeholder="e.g. 21000"
              />
              <span className="mt-1 block font-body text-body-xs text-text-secondary">
                Cap on the total cart value a customer can add on the tenant subdomain.
              </span>
            </label>
            {!terminal ? (
              <Button variant="ghost" size="sm" onClick={saveMeta}>Save organisation + caps</Button>
            ) : null}
          </section>

          <section className="border-t border-border/40 pt-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <h3 className="font-heading text-h4 text-charcoal">Lines ({editLines.length})</h3>
              <span className="font-body text-body-xs text-text-secondary">Total qty: {totalCount}</span>
            </div>
            <div className="space-y-2">
              {editLines.map((l, i) => (
                <LineRowEditor
                  key={i}
                  line={l}
                  disabled={terminal}
                  onChange={(next) => setEditLines((arr) => arr.map((x, j) => (j === i ? next : x)))}
                  onRemove={() => setEditLines((arr) => arr.filter((_, j) => j !== i))}
                />
              ))}
              {!terminal ? (
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setEditLines((arr) => [...arr, blankLine()])}
                >
                  + Add line
                </Button>
              ) : null}
            </div>
            {!terminal ? (
              <Button variant="ghost" size="sm" onClick={saveLines}>Save lines</Button>
            ) : null}
          </section>
        </div>
      )}
    </Modal>
  )
}

/* ───────────────── inline helpers ───────────────── */

interface EditLine {
  readonly id: string | null
  readonly variant_id: string
  readonly product_id: string
  readonly product_name: string
  readonly axes_label: string
  readonly quantity: number | null
}

function blankLine(): EditLine {
  return { id: null, variant_id: '', product_id: '', product_name: '', axes_label: '', quantity: 1 }
}

function toEditLine(item: BuildRequestItemDto): EditLine {
  return {
    id: item.id,
    variant_id: item.variant_id,
    product_id: item.product_id,
    product_name: item.product_name,
    axes_label: item.axes_label ?? '',
    quantity: item.quantity
  }
}

function LineRowEditor({
  line, disabled, onChange, onRemove
}: {
  readonly line: EditLine
  readonly disabled: boolean
  readonly onChange: (next: EditLine) => void
  readonly onRemove: () => void
}): JSX.Element {
  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <div className="col-span-3">
        <Input
          value={line.product_name}
          disabled={disabled}
          onChange={(e) => onChange({ ...line, product_name: e.target.value })}
          placeholder="Product name"
        />
      </div>
      <div className="col-span-2">
        <Input
          value={line.axes_label}
          disabled={disabled}
          onChange={(e) => onChange({ ...line, axes_label: e.target.value })}
          placeholder="Axes (e.g. Size: M)"
        />
      </div>
      <div className="col-span-3 font-mono text-body-xs">
        <Input
          value={line.variant_id}
          disabled={disabled}
          onChange={(e) => onChange({ ...line, variant_id: e.target.value })}
          placeholder="variant_id"
        />
      </div>
      <div className="col-span-2 font-mono text-body-xs">
        <Input
          value={line.product_id}
          disabled={disabled}
          onChange={(e) => onChange({ ...line, product_id: e.target.value })}
          placeholder="product_id"
        />
      </div>
      <div className="col-span-1">
        <Input
          type="number"
          min={1}
          step={1}
          value={line.quantity ?? ''}
          disabled={disabled}
          onChange={(e) => {
            const n = Number(e.target.value)
            onChange({ ...line, quantity: Number.isFinite(n) ? n : null })
          }}
        />
      </div>
      <div className="col-span-1 text-right">
        {!disabled ? (
          <button
            type="button"
            onClick={onRemove}
            className="text-body-xs text-terra hover:underline"
          >
            Remove
          </button>
        ) : null}
      </div>
    </div>
  )
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="font-body text-body-xs uppercase tracking-wide text-text-secondary">{label}</div>
      <div className="mt-1 font-body text-body-sm text-charcoal">{children}</div>
    </div>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
}

/** Parse the Max-cart-value rupee input. Returns:
 *    - null            when empty (operator hasn't set it yet)
 *    - paisa integer   on a valid non-negative number
 *    - 'invalid'       on garbage / negative / non-finite input
 *  Decimal rupees (e.g. 21000.50) are accepted and rounded to paisa. */
function cartValueAsPaisa(rupees: string): number | null | 'invalid' {
  const trimmed = rupees.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return 'invalid'
  return Math.round(n * 100)
}
